import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { AuthUser, ComplianceStatusResponse } from '@fetmode/types';
import { useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';
import { TipModal, TIP_PRESETS } from './TipModal.js';

export { TIP_PRESETS } from './TipModal.js';

export interface TipPanelProps {
  user: AuthUser | null;
  compliance: ComplianceStatusResponse | null;
  canTip: boolean;
  coinBalance: number;
  tipAmount: string;
  onTipAmountChange: (value: string) => void;
  tipMessage: string;
  onTipMessageChange: (value: string) => void;
  isTipping: boolean;
  tipError: string | null;
  tipSuccess: boolean;
  onSendTip: (amountCoins: number) => Promise<boolean>;
  /** When another action expands, collapse this slide-out. */
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
}

export function TipPanel({
  user,
  compliance,
  canTip,
  coinBalance,
  tipAmount,
  onTipAmountChange,
  tipMessage,
  onTipMessageChange,
  isTipping,
  tipError,
  tipSuccess,
  onSendTip,
  expanded,
  onExpandedChange,
}: TipPanelProps): ReactElement {
  const { requireAuth } = useAuthModal();
  const { requireCoins } = useCoinShop();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);

  const parsedTipAmount = Number.parseInt(tipAmount, 10);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node) || !groupRef.current?.contains(target)) {
        onExpandedChange(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [expanded, onExpandedChange]);

  async function handleSendFromModal(): Promise<void> {
    if (!Number.isFinite(parsedTipAmount)) {
      return;
    }

    const sent = await onSendTip(parsedTipAmount);
    if (sent) {
      setIsModalOpen(false);
    }
  }

  return (
    <div className="show-room__tip-group" ref={groupRef}>
      <Button
        label={t('common.show.tipButton')}
        variant="tip"
        onClick={() => {
          requireAuth(() => {
            onExpandedChange(!expanded);
          }, 'tip');
        }}
      />

      <div
        className={`show-room__tip-slide${expanded ? ' show-room__tip-slide--open' : ''}`}
        aria-hidden={!expanded}
      >
        <div className="show-room__tip-slide-inner" aria-label={t('common.show.tipPresets')}>
          {TIP_PRESETS.map((preset) => (
            <Button
              key={preset}
              label={String(preset)}
              variant="chip"
              disabled={isTipping}
              onClick={() => {
                requireAuth(() => {
                  if (!requireCoins(coinBalance, preset, () => {
                    void onSendTip(preset);
                  })) {
                    return;
                  }
                  void onSendTip(preset);
                }, 'tip');
              }}
            />
          ))}
          <Button
            label={t('common.show.tipCustom')}
            variant="chip"
            onClick={() => {
              requireAuth(() => {
                onExpandedChange(false);
                setIsModalOpen(true);
              }, 'tip');
            }}
          />
        </div>
      </div>

      <TipModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        user={user}
        compliance={compliance}
        canTip={canTip}
        coinBalance={coinBalance}
        tipAmount={tipAmount}
        onTipAmountChange={onTipAmountChange}
        tipMessage={tipMessage}
        onTipMessageChange={onTipMessageChange}
        isTipping={isTipping}
        tipError={tipError}
        tipSuccess={tipSuccess}
        onSend={() => {
          void handleSendFromModal();
        }}
      />
    </div>
  );
}
